<template>
  <div class="purchase-return-detail">
    <div class="page-header">
      <h2>{{ $t("wholesaler.PurchaseReturnDetail.title") }}</h2>
      <el-button @click="goBack">{{ $t("wholesaler.PurchaseReturnDetail.back") }}</el-button>
    </div>

    <el-card class="box-card" v-loading="loading">
      <div slot="header" class="card-header">
        <span>{{ $t("wholesaler.PurchaseReturnDetail.basicInfo") }}</span>
      </div>

      <el-descriptions :column="3" border>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.returnNo')">{{ detail.returnNo }}</el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.purchaseNo')">{{ detail.purchaseNo }}</el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.status')">
          <el-tag :type="getStatusType(detail.status)">
            {{ getStatusText(detail.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.supplier')">{{ detail.supplierName }}</el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.createdAt')">{{ detail.createdAt }}</el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.refundMethod')">{{ getRefundTypeText(detail.refundType) }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-card class="box-card">
      <div slot="header" class="card-header">
        <span>{{ $t("wholesaler.PurchaseReturnDetail.products") }}</span>
      </div>

      <el-table :data="detail.items" border style="width: 100%">
        <el-table-column prop="productName" :label="$t('wholesaler.PurchaseReturnDetail.productName')" />
        <el-table-column prop="productSku" label="SKU" />
        <el-table-column prop="originalQuantity" :label="$t('wholesaler.PurchaseReturnDetail.originalQuantity')" width="100" />
        <el-table-column prop="returnQuantity" :label="$t('wholesaler.PurchaseReturnDetail.returnQuantity')" width="100" />
        <el-table-column prop="unitPrice" :label="$t('wholesaler.PurchaseReturnDetail.unitPrice')" width="120">
          <template slot-scope="scope">
            ¥{{ scope.row.unitPrice.toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.subtotal')" width="120">
          <template slot-scope="scope">
            ¥{{ scope.row.returnAmount.toFixed(2) }}
          </template>
        </el-table-column>
      </el-table>

      <div class="amount-summary">
        <div class="summary-item">
          <span>{{ $t("wholesaler.PurchaseReturnDetail.returnAmount") }}：</span>
          <span>¥{{ detail.totalReturnAmount.toFixed(2) }}</span>
        </div>
        <div class="summary-item">
          <span>GST (9%)：</span>
          <span>¥{{ detail.gstAmount.toFixed(2) }}</span>
        </div>
        <div class="summary-item total">
          <span>{{ $t("wholesaler.PurchaseReturnDetail.totalRefund") }}：</span>
          <span class="amount">¥{{ detail.totalRefundAmount.toFixed(2) }}</span>
        </div>
      </div>
    </el-card>

    <el-card class="box-card">
      <div slot="header" class="card-header">
        <span>{{ $t("wholesaler.PurchaseReturnDetail.reason") }}</span>
      </div>

      <el-descriptions :column="1" border>
        <el-descriptions-item label="原因类型">{{ getReasonText(detail.reasonCode) }}</el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.description')">{{ detail.reasonText }}</el-descriptions-item>
        <el-descriptions-item :label="$t('wholesaler.PurchaseReturnDetail.evidence')" v-if="detail.evidenceUrls && detail.evidenceUrls.length > 0">
          <div class="evidence-images">
            <el-image
              v-for="(url, index) in detail.evidenceUrls"
              :key="index"
              :src="url"
              :preview-src-list="detail.evidenceUrls"
              style="width: 100px; height: 100px; margin-right: 10px;"
              fit="cover"
            />
          </div>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-card class="box-card" v-if="detail.purchasePayments && detail.purchasePayments.length > 0">
      <div slot="header" class="card-header">
        <span>{{ $t("wholesaler.PurchaseReturnDetail.paymentRecords") }}</span>
      </div>

      <el-table :data="detail.purchasePayments" border style="width: 100%">
        <el-table-column prop="paymentDate" :label="$t('wholesaler.PurchaseReturnDetail.paymentDate')" width="120">
          <template slot-scope="scope">
            {{ scope.row.paymentDate }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.paymentAmount')" width="120">
          <template slot-scope="scope">
            <span style="color: #67c23a;">¥{{ scope.row.paymentAmount.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.paymentMethod')" width="100">
          <template slot-scope="scope">
            {{ getPaymentMethodText(scope.row.paymentMethod) }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('wholesaler.PurchaseReturnDetail.remark')" />
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.proof')" width="100">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.proofUrl"
              :src="scope.row.proofUrl"
              :preview-src-list="[scope.row.proofUrl]"
              style="width: 60px; height: 60px;"
              fit="cover"
            />
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" :label="$t('wholesaler.PurchaseReturnDetail.createdAt')" width="160" />
      </el-table>
    </el-card>

    <el-card class="box-card" v-if="detail.refundRecords && detail.refundRecords.length > 0">
      <div slot="header" class="card-header">
        <span>{{ $t("wholesaler.PurchaseReturnDetail.refundRecords") }}</span>
      </div>

      <el-table :data="detail.refundRecords" border style="width: 100%">
        <el-table-column prop="refundNo" :label="$t('wholesaler.PurchaseReturnDetail.refundNo')" width="200" />
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.refundAmount')" width="120">
          <template slot-scope="scope">
            <span style="color: #f56c6c;">¥{{ scope.row.amount.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.refundMethod')" width="100">
          <template slot-scope="scope">
            {{ getRefundTypeText(scope.row.refundType) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.status')" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.refundStatus === 'completed' ? 'success' : 'warning'" size="small">
              {{ scope.row.refundStatus === 'completed' ? $t("wholesaler.PurchaseReturnDetail.completed") : $t("wholesaler.PurchaseReturnDetail.pending") }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="operatorNote" :label="$t('wholesaler.PurchaseReturnDetail.remark')" />
        <el-table-column :label="$t('wholesaler.PurchaseReturnDetail.refundTime')" width="160">
          <template slot-scope="scope">
            {{ scope.row.refundedAt || scope.row.createdAt }}
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card class="box-card" v-if="detail.actionLogs && detail.actionLogs.length > 0">
      <div slot="header" class="card-header">
        <span>{{ $t("wholesaler.PurchaseReturnDetail.actionLogs") }}</span>
      </div>

      <el-timeline>
        <el-timeline-item
          v-for="log in detail.actionLogs"
          :key="log.id"
          :timestamp="log.createdAt"
          placement="top"
        >
          <el-card>
            <p><strong>{{ getActionText(log.actionType) }}</strong></p>
            <p v-if="log.actionDesc">{{ log.actionDesc }}</p>
            <p v-if="log.operatorName">{{ $t("wholesaler.PurchaseReturnDetail.operator") }}：{{ log.operatorName }}</p>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </el-card>

    <div class="action-footer" v-if="showActions">
      <el-button
        v-if="detail.status === 'PENDING_REVIEW'"
        type="success"
        @click="approveReturn"
      >
        {{ $t("wholesaler.PurchaseReturnDetail.approve") }}
      </el-button>
      <el-button
        v-if="detail.status === 'PENDING_REVIEW'"
        type="danger"
        @click="rejectReturn"
      >
        {{ $t("wholesaler.PurchaseReturnDetail.reject") }}
      </el-button>
      <el-button
        v-if="detail.status === 'REVIEWED'"
        type="primary"
        @click="receiveReturn"
      >
        {{ $t("wholesaler.PurchaseReturnDetail.receive") }}
      </el-button>
      <el-button
        v-if="detail.status === 'RECEIVED'"
        type="success"
        @click="refundReturn"
      >
        {{ $t("wholesaler.PurchaseReturnDetail.refund") }}
      </el-button>
      <el-button
        v-if="canCancel"
        @click="cancelReturn"
      >
        {{ $t("wholesaler.PurchaseReturnDetail.cancelReturn") }}
      </el-button>
    </div>
  </div>
</template>

<script>
import {
  getPurchaseReturnDetail,
  approvePurchaseReturn,
  rejectPurchaseReturn,
  receivePurchaseReturn,
  refundPurchaseReturn,
  cancelPurchaseReturn
} from '@/api/purchaseReturn'

export default {
  name: 'PurchaseReturnDetail',
  data() {
    return {
      loading: false,
      detail: {
        items: [],
        actionLogs: [],
        evidenceUrls: []
      }
    }
  },
  computed: {
    returnId() {
      return this.$route.params.id
    },
    showActions() {
      return ['PENDING_REVIEW', 'REVIEWED', 'RECEIVED'].includes(this.detail.status)
    },
    canCancel() {
      return ['PENDING_REVIEW', 'REVIEWED', 'RECEIVED'].includes(this.detail.status)
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    async loadDetail() {
      this.loading = true
      try {
        const res = await getPurchaseReturnDetail(this.returnId)
        this.detail = res.data
        
        // 解析 evidenceUrls
        if (typeof this.detail.evidenceUrls === 'string') {
          try {
            this.detail.evidenceUrls = JSON.parse(this.detail.evidenceUrls)
          } catch (e) {
            this.detail.evidenceUrls = []
          }
        }
      } catch (error) {
        this.$message.error('加载详情失败')
      } finally {
        this.loading = false
      }
    },
    async approveReturn() {
      try {
        await this.$confirm('确认通过该退货申请？', this.$t('wholesaler.PurchaseReturnDetail.tip'), {
          type: 'warning'
        })
        await approvePurchaseReturn(this.returnId, { approved: true })
        this.$message.success('{{ $t("wholesaler.PurchaseReturnDetail.approve") }}')
        this.loadDetail()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('wholesaler.PurchaseReturnDetail.approveFailed'))
        }
      }
    },
    async rejectReturn() {
      try {
        const { value } = await this.$prompt(this.$t('wholesaler.PurchaseReturnDetail.enterRejectReason'), '{{ $t("wholesaler.PurchaseReturnDetail.reject") }}', {
          confirmButtonText: this.$t('wholesaler.PurchaseReturnDetail.confirm'),
          cancelButtonText: this.$t('wholesaler.PurchaseReturnDetail.cancel'),
          inputPattern: /\S+/,
          inputErrorMessage: this.$t('wholesaler.PurchaseReturnDetail.required')
        })
        await rejectPurchaseReturn(this.returnId, { reason: value })
        this.$message.success(this.$t('wholesaler.PurchaseReturnDetail.rejected'))
        this.loadDetail()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('wholesaler.PurchaseReturnDetail.operationFailed'))
        }
      }
    },
    async receiveReturn() {
      try {
        await this.$confirm(this.$t('wholesaler.PurchaseReturnDetail.confirmReceive'), this.$t('wholesaler.PurchaseReturnDetail.tip'), {
          type: 'warning'
        })
        await receivePurchaseReturn(this.returnId)
        this.$message.success('已{{ $t("wholesaler.PurchaseReturnDetail.receive") }}')
        this.loadDetail()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('wholesaler.PurchaseReturnDetail.operationFailed'))
        }
      }
    },
    async refundReturn() {
      try {
        const { value } = await this.$prompt(this.$t('wholesaler.PurchaseReturnDetail.enterRefundAmount'), '{{ $t("wholesaler.PurchaseReturnDetail.refund") }}', {
          confirmButtonText: this.$t('wholesaler.PurchaseReturnDetail.confirm'),
          cancelButtonText: this.$t('wholesaler.PurchaseReturnDetail.cancel'),
          inputValue: this.detail.totalRefundAmount.toFixed(2),
          inputPattern: /^\d+(\.\d{1,2})?$/,
          inputErrorMessage: this.$t('wholesaler.PurchaseReturnDetail.invalidAmount'),
          type: 'number'
        })
        
        const refundAmount = parseFloat(value)
        if (isNaN(refundAmount) || refundAmount <= 0) {
          this.$message.error(this.$t('wholesaler.PurchaseReturnDetail.amountMustBePositive'))
          return
        }
        
        await refundPurchaseReturn(this.returnId, null, null, null, null, refundAmount)
        this.$message.success(this.$t('wholesaler.PurchaseReturnDetail.refundSuccess'))
        this.loadDetail()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('wholesaler.PurchaseReturnDetail.refundFailed'))
        }
      }
    },
    async cancelReturn() {
      try {
        await this.$confirm(this.$t('wholesaler.PurchaseReturnDetail.confirmCancel'), this.$t('wholesaler.PurchaseReturnDetail.tip'), {
          type: 'warning'
        })
        await cancelPurchaseReturn(this.returnId)
        this.$message.success(this.$t('wholesaler.PurchaseReturnDetail.cancelled'))
        this.$router.push('/wholesaler/purchase-return-list')
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('wholesaler.PurchaseReturnDetail.cancelFailed'))
        }
      }
    },
    getStatusType(status) {
      const map = {
        PENDING_REVIEW: 'warning',
        REVIEWED: 'primary',
        RECEIVED: 'info',
        REFUNDED: 'success',
        COMPLETED: 'success',
        REJECTED: 'danger',
        CANCELLED: 'info'
      }
      return map[status] || 'info'
    },
    getStatusText(status) {
      const map = {
        PENDING_REVIEW: this.$t('wholesaler.PurchaseReturnDetail.statusPendingReview'),
        REVIEWED: this.$t('wholesaler.PurchaseReturnDetail.statusReviewed'),
        RECEIVED: this.$t('wholesaler.PurchaseReturnDetail.statusReceived'),
        REFUNDED: this.$t('wholesaler.PurchaseReturnDetail.statusRefunded'),
        COMPLETED: this.$t('wholesaler.PurchaseReturnDetail.statusCompleted'),
        REJECTED: this.$t('wholesaler.PurchaseReturnDetail.statusRejected'),
        CANCELLED: this.$t('wholesaler.PurchaseReturnDetail.statusCancelled')
      }
      return map[status] || status
    },
    getRefundTypeText(type) {
      const map = {
        ORIGINAL: this.$t('wholesaler.PurchaseReturnDetail.originalReturn'),
        OFFLINE: this.$t('wholesaler.PurchaseReturnDetail.offlineRefund'),
        CASH: this.$t('wholesaler.PurchaseReturnDetail.refundCash'),
        CREDIT_CARD: this.$t('wholesaler.PurchaseReturnDetail.refundCreditCard'),
        BANK_TRANSFER: this.$t('wholesaler.PurchaseReturnDetail.refundTransfer'),
        PAYNOW: 'PayNow'
      }
      return map[type] || type
    },
    getPaymentMethodText(method) {
      const map = {
        CASH: this.$t('wholesaler.PurchaseReturnDetail.refundCash'),
        CREDIT_CARD: this.$t('wholesaler.PurchaseReturnDetail.refundCreditCard'),
        BANK_TRANSFER: this.$t('wholesaler.PurchaseReturnDetail.refundTransfer'),
        ONLINE_BANKING: this.$t('wholesaler.PurchaseReturnDetail.onlineBanking'),
        PAYNOW: 'PayNow'
      }
      return map[method] || method
    },
    getReasonText(code) {
      const map = {
        QUALITY_ISSUE: this.$t('wholesaler.PurchaseReturnDetail.reasonQualityIssue'),
        WRONG_ITEM: this.$t('wholesaler.PurchaseReturnDetail.reasonWrongItem'),
        WRONG_QUANTITY: this.$t('wholesaler.PurchaseReturnDetail.wrongQuantity'),
        DAMAGED: this.$t('wholesaler.PurchaseReturnDetail.reasonDamaged'),
        EXPIRED: this.$t('wholesaler.PurchaseReturnDetail.reasonExpired'),
        SPEC_MISMATCH: this.$t('wholesaler.PurchaseReturnDetail.reasonSpecMismatch'),
        OTHER: this.$t('wholesaler.PurchaseReturnDetail.reasonOther')
      }
      return map[code] || code
    },
    getActionText(type) {
      const map = {
        CREATE: this.$t('wholesaler.PurchaseReturnDetail.create'),
        APPROVE: '{{ $t("wholesaler.PurchaseReturnDetail.approve") }}',
        REJECT: '{{ $t("wholesaler.PurchaseReturnDetail.reject") }}',
        RECEIVE: '{{ $t("wholesaler.PurchaseReturnDetail.receive") }}',
        REFUND: '{{ $t("wholesaler.PurchaseReturnDetail.refund") }}',
        CANCEL: '{{ $t("wholesaler.PurchaseReturnDetail.cancelReturn") }}'
      }
      return map[type] || type
    },
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.purchase-return-detail {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
}

.box-card {
  margin-bottom: 20px;
}

.card-header {
  font-weight: bold;
}

.amount-summary {
  margin-top: 20px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 14px;
}

.summary-item.total {
  border-top: 1px solid #dcdfe6;
  padding-top: 10px;
  margin-top: 10px;
  font-size: 16px;
  font-weight: bold;
}

.summary-item .amount {
  color: #f56c6c;
  font-size: 20px;
}

.evidence-images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.action-footer {
  text-align: center;
  margin-top: 30px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.action-footer .el-button {
  margin: 0 10px;
}
</style>
